import React from 'react';
import { Link } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import { formatCount, joinMeta } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';

/**
 * WHAT MOVES, before anything does (owner ruling D10; keepr-api § Moving
 * cards and items): the card and each child card that goes with it, as a
 * badge with its record count, the total, and — from a dry run — what stops
 * working (`needsALook`), each with a door to where it is fixed.
 *
 *   preview = { cards: [{ _id, name, itemCount }], itemTotal, needsALook? }
 *
 * `cardsById` dresses each row in the card's own identity; `sourceId` is the
 * collection the cards leave, whose Sharing tab the link and grant rows open.
 */

/** "Move 3 cards and 260 items" — the confirming primary's words. */
export function moveVerb(preview, verb = 'Move') {
	const cards = Array.isArray(preview?.cards) ? preview.cards.length : 0;
	const items = Number(preview?.itemTotal) || 0;
	const cardText = cards === 1 ? '1 card' : `${cards} cards`;
	return items ? `${verb} ${cardText} and ${formatCount(items, 'item')}` : `${verb} ${cardText}`;
}

/** One `needsALook` entry as a sentence and a door. */
export function describeNeedsALook(entry, { sourceId = null, cardsById = {} } = {}) {
	const sharing = sourceId ? `/collections/${sourceId}/form?tab=sharing` : null;
	if (entry?.kind === 'shareLink') {
		const name = entry.name ? `"${entry.name}"` : 'A share link';
		return {
			text: entry.reason === 'record_leaves'
				? `${name} links to a record that moves; it stops working.`
				: `${name} shares a card that moves; it stops working.`,
			to: sharing,
			door: 'Share links',
		};
	}
	if (entry?.kind === 'grant') {
		const card = cardsById[normalizeId(entry.card_id)];
		return {
			text: `Access granted on ${card?.name ? `"${card.name}"` : 'a moving card'} here is removed: that card will no longer be used here.`,
			to: sharing,
			door: 'Sharing',
		};
	}
	if (entry?.kind === 'lookup') {
		const id = normalizeId(entry.cardId);
		return {
			text: `"${entry.cardName || 'A card'}" looks up a moving card${entry.element ? ` (${entry.element})` : ''}; it will no longer reach the records that move.`,
			to: id ? `/cards/${id}` : null,
			door: 'Open card',
		};
	}
	return { text: 'Something else here depends on these cards.', to: null, door: null };
}

export default function CardMovePreview({ preview, cardsById = {}, sourceId = null, emptyText = null }) {
	const cards = Array.isArray(preview?.cards) ? preview.cards : [];
	const needs = Array.isArray(preview?.needsALook) ? preview.needsALook : [];
	if (!cards.length) return emptyText ? <p className="meta-line">{emptyText}</p> : null;
	// Moving UP moves no record (the card becomes usable family-wide), so a
	// per-card count would read as "this card has no records".
	const up = preview?.direction === 'up';
	return (
		<div className="card-move-preview" data-testid="card-move-preview">
			<ul className="card-move-preview__rows">
				{cards.map(card => {
					const id = normalizeId(card?._id);
					const def = cardsById[id];
					return (
						<li key={id} className="card-move-preview__row">
							<RecordBadge kind="card" record={def || { name: card?.name }} title={def?.name || card?.name || 'Card'} size="xs" />
							{up ? null : <span className="meta-line">{formatCount(card?.itemCount, 'item') || 'No items'}</span>}
						</li>
					);
				})}
			</ul>
			<p className="card-move-preview__total meta-line">
				{joinMeta(formatCount(cards.length, 'card'), up ? 'their records stay where they are' : (formatCount(preview?.itemTotal, 'item') || 'no items'))}
			</p>
			{needs.length ? (
				<div className="card-move-preview__look" role="note">
					<div className="micro-label">Needs a look</div>
					<ul>
						{needs.map((entry, index) => {
							const { text, to, door } = describeNeedsALook(entry, { sourceId, cardsById });
							return (
								<li key={normalizeId(entry?._id) || `${entry?.kind}-${index}`}>
									<span>{text}</span>
									{to && door ? <> <Link to={to}>{door}</Link></> : null}
								</li>
							);
						})}
					</ul>
				</div>
			) : null}
		</div>
	);
}
